// @flow
import ko from 'knockout';
import _ from 'lodash';

import type {
	AddressOptions,
	AddressSettlementOptions,
	AddressStreetOptions,
	AddressHouseOptions,
} from './types.flow.js';

import {
	JSableOmitPrivate,
} from 'jsable';

import kladrUtils from 'kladr-utils';
import ObservableMap from 'observable-map';
import type { IObservableMap } from 'observable-map';
import type { CountryInterface } from './contracts.flow';

class AddressEntity
extends JSableOmitPrivate {
	constructor (dictionary: IObservableMap) {
		super();

		this._dictionary = dictionary;

		this.Kladr = ko.observable()
			.extend({
				toFromJS: true,
			});

		this.Name = ko.pureComputed({
			read: () => dictionary.get(this.Kladr()),
			write: name => {
				if (!name) {
					this.Kladr(null);
				} else {
					const id = dictionary.get(name);
					if (id) {
						this.Kladr(id);
					}
				}
			},
		})
			.extend({
				toJS: true,
			});
	}

	fromJS (js: any) {
		if (js && js.Kladr && js.Name) {
			this._dictionary.bisetAbsent(
				js.Kladr,
				js.Name,
			);
		}

		return super.fromJS(...arguments);
	}

	repr () {
		return this.Name();
	}
}


export class AddressHouse
extends AddressEntity
implements AddressHouseInterface {
	constructor (options: AddressHouseOptions) {
		super(options.dictionary);
	}
}


export class AddressStreet
extends AddressEntity
implements AddressStreetInterface {
	constructor (options: AddressStreetOptions) {
		const {
			address,
			dictionary,
		} = options;

		super(dictionary);

		this.Kladr = ko.map(this.Kladr, {
			mapRead: _.flowRight(
				v => address.HasStreets() ? v : null,
				v => {
					const settlementKladr = address.Settlement.Kladr();
					if (!settlementKladr) {
						return v;
					}
					return kladrUtils.contains(settlementKladr, v)
						?	v
						: null;
				},
			),
		})
			.extend({
				toFromJS: true,
			});
	}
}


export class AddressSettlement
extends AddressEntity
implements AddressSettlementInterface {
	constructor (options: AddressSettlementOptions) {
		const {
			dictionary = new ObservableMap(),
		} = options;

		super(dictionary);
	}
}


export class Address
extends JSableOmitPrivate
implements AddressInterface {
	constructor (options: AddressOptions) {
		options = _.defaultsDeep({}, options, {
			dictionaries: {
				kladr: {
					streets: new ObservableMap(),
					settlements: new ObservableMap(),
				},
			},
		});

		super();

		const {
			kladr,
		} = options.dictionaries;

		this.Kladr = ko.pureComputed(() => kladrUtils.mostAccurate([
			this.Street.Kladr(),
			this.Settlement.Kladr(),
		]))
			.extend({
				toJS: true,
			});

		const addressSettlementOptions: AddressSettlementOptions = {
			dictionary: kladr.settlements,
		};

		this.Settlement = new AddressSettlement(addressSettlementOptions);

		this.HasStreets = ko.observable()
			.extend({
				toFromJS: true,
			});

		const addressStreetsOptions: AddressStreetOptions = {
			dictionary: kladr.streets,
			address: this,
		};

		this.Street = new AddressStreet(addressStreetsOptions);

		this.House = ko.observable()
			.extend({
				toFromJS: true,
			})
			.extend({
				repr: _.identity,
			});

		this.Apartement = ko.observable()
			.extend({
				toFromJS: true,
			})
			.extend({
				repr: _.identity,
			});
	}

	repr () {
		const settlement = this.Settlement.repr();
		const street = this.Street.repr();
		const house = this.House.repr();
		const apartement = this.Apartement.repr();
		return _([
			settlement,
			street,
			house,
			apartement,
		])
			.map(_.trim)
			.filter(_.identity)
			.join(', ');
	}
}

export class Country extends JSableOmitPrivate implements CountryInterface {
	constructor (options = {}) {
		super(options);

		this._dictionary = options.dictionary || new ObservableMap();

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.Name = ko.pureComputed({
			read: () => this._dictionary.get(this.ID()) || null,
			write: name => {
				if (!name) {
					this.ID(null);
				} else {
					const id = this._dictionary.get(name);
					if (id) {
						this.ID(id);
					} else {
						this.ID(null);
					}
				}
			},
		})
			.extend({ toFromJS: true });
	}

	fromJS (js: { ID: number, Name: string }) {
		if (js && js.ID && js.Name) {
			this._dictionary.bisetAbsent(
				js.ID,
				js.Name,
			);
		}

		return super.fromJS(...arguments);
	}

	repr () {
		return this.Name();
	}
}
